import type { BufferGeometry, Material, Object3D } from 'three';

type DisposableValue = {
  dispose?: () => void;
};

type ObjectWithDisposableResources = Object3D & {
  geometry?: BufferGeometry;
  material?: Material | Material[];
};

function disposeValue(value: unknown) {
  if (Array.isArray(value)) {
    for (const entry of value) {
      disposeValue(entry);
    }
    return;
  }

  if (!value || typeof value !== 'object') return;

  const disposable = value as DisposableValue;
  if (typeof disposable.dispose === 'function') {
    disposable.dispose();
  }
}

function disposeMaterial(
  material: Material | Material[],
  disposedMaterials: Set<Material>,
) {
  if (Array.isArray(material)) {
    for (const entry of material) {
      disposeMaterial(entry, disposedMaterials);
    }
    return;
  }

  if (disposedMaterials.has(material)) return;
  disposedMaterials.add(material);

  for (const value of Object.values(material)) {
    disposeValue(value);
  }

  const uniforms = (
    material as { uniforms?: Record<string, { value?: unknown }> }
  ).uniforms;
  if (uniforms) {
    for (const uniform of Object.values(uniforms)) {
      disposeValue(uniform.value);
    }
  }

  material.dispose();
}

export function disposeCartographerObject3D(obj: Object3D | null) {
  if (!obj) return;

  if (obj.parent) {
    obj.parent.remove(obj);
  }

  const disposedGeometries = new Set<BufferGeometry>();
  const disposedMaterials = new Set<Material>();

  obj.traverse((object) => {
    const resourceObject = object as ObjectWithDisposableResources;
    if (
      resourceObject.geometry &&
      !disposedGeometries.has(resourceObject.geometry)
    ) {
      resourceObject.geometry.dispose();
      disposedGeometries.add(resourceObject.geometry);
    }

    if (resourceObject.material) {
      disposeMaterial(resourceObject.material, disposedMaterials);
    }
  });
}
